import { IWebClient } from "@cd2/skeem-client"
import { getClearancePriceForProduct } from "../../shared/operatingRegions"
import {
  fetchBulkPricesForCustomer,
  IProductPrice as ICustomerPrice,
} from "./fetchPricesForCustomer"

export interface IProductPrice {
  sourceName: string
  sourceType: string
  customerPrice: number | null
  customerPriceDiscountPercent: null | number
  usingNonStandardPrice: boolean
  standardPrice?: number | null
  currency_name?: string
  applyOpportunityCertificateBreakdown: boolean
  requiresApprovalForChange: boolean
  customerPrices?: any[]
}

// Quote/order rows and price loaders each ask for one product at a time, often all
// at once when a page mounts. Requests made in the same tick are collected per
// customer and fetched with one getBulkProductSuggestedPrice call, and identical
// requests that are still in flight share a promise. Nothing is kept once settled.
type IPendingPriceBatch = {
  productIds: Set<string>
  result: Promise<Record<string, IProductPrice | null>>
}
const pendingPriceBatches = new Map<string, IPendingPriceBatch>()
const inFlightPrices = new Map<string, Promise<IProductPrice | null>>()

export function getProductSuggestedPrice(
  skeem: IWebClient,
  quote: { customer?: { id: string } },
  productId: string,
): Promise<IProductPrice | null> {
  const customerId = quote.customer?.id
  if (!customerId) {
    return Promise.resolve(null)
  }

  const key = `${customerId}:${productId}`
  const existing = inFlightPrices.get(key)
  if (existing) {
    return existing
  }

  let batch = pendingPriceBatches.get(customerId)
  if (!batch) {
    const productIds = new Set<string>()
    const result = new Promise<Record<string, IProductPrice | null>>((resolve, reject) => {
      setTimeout(() => {
        pendingPriceBatches.delete(customerId)
        getBulkProductSuggestedPrice(skeem, { customer: { id: customerId } }, [
          ...productIds,
        ]).then(resolve, reject)
      }, 0)
    })
    batch = { productIds, result }
    pendingPriceBatches.set(customerId, batch)
  }
  batch.productIds.add(productId)

  const promise = batch.result.then(prices => prices[productId] ?? null)
  inFlightPrices.set(key, promise)
  const clear = () => inFlightPrices.delete(key)
  promise.then(clear, clear)
  return promise
}

export async function getBulkProductSuggestedPrice(
  skeem: IWebClient,
  quote: { customer?: { id: string } },
  productIds: string[],
): Promise<Record<string, IProductPrice | null>> {
  if (!quote.customer) {
    return {}
  }

  const clearanceProducts = await skeem.fetch2<
    {
      id: string
      branch?: { id: string }
      product_master?: {
        clearance_price_uk_pennies?: number | null
        clearance_price_ireland_pennies?: number | null
        clearance_was_price_uk_pennies?: number | null
        clearance_was_price_ireland_pennies?: number | null
      }
    }[]
  >({
    model: {
      name: "products",
      attributes: [
        "branch",
        {
          name: "product_master",
          attributes: [
            "clearance_price_uk_pennies",
            "clearance_price_ireland_pennies",
            "clearance_was_price_uk_pennies",
            "clearance_was_price_ireland_pennies",
          ],
        },
      ],
      filter: { matches: { id: { or: productIds } } },
    },
  })

  const productPrices: Record<string, IProductPrice | null> = {}
  const remainingProductIds: string[] = []

  const clearanceProductIds: string[] = []

  for (const product of clearanceProducts) {
    const clearancePrice = getClearancePriceForProduct(product)
    if (clearancePrice != null) {
      clearanceProductIds.push(product.id)
    } else {
      remainingProductIds.push(product.id)
    }
  }

  if (clearanceProductIds.length > 0) {
    const clearancePricesByProduct = await fetchBulkPricesForCustomer(
      skeem,
      clearanceProductIds,
      quote.customer.id,
    )

    for (const product of clearanceProducts) {
      const clearancePrice = getClearancePriceForProduct(product)
      if (clearancePrice == null) {
        continue
      }
      const customerPrices = clearancePricesByProduct[product.id] ?? []
      const nonClearancePrices = customerPrices.filter(p => p.sourceType !== "clearance")
      const customerCompareAtPrice =
        suggestedPriceFilter(nonClearancePrices)?.customerPrice ||
        nonClearancePrices.find(p => p.name.toLowerCase() === "standard")?.price_pennies ||
        null
      const wasPrice = clearancePrice.was_price_pennies
      // Prefer the customer's own price; fall back to the master was-price field.
      const compareAtPrice = customerCompareAtPrice || wasPrice
      const discountPercent =
        compareAtPrice != null && compareAtPrice > clearancePrice.price_pennies
          ? Math.round((1 - clearancePrice.price_pennies / compareAtPrice) * 100)
          : null

      productPrices[product.id] = {
        sourceName: "Deals",
        sourceType: "clearance",
        customerPrice: clearancePrice.price_pennies,
        customerPriceDiscountPercent: discountPercent,
        usingNonStandardPrice: true,
        standardPrice: compareAtPrice,
        currency_name: clearancePrice.currency_name,
        applyOpportunityCertificateBreakdown: false,
        requiresApprovalForChange: false,
      }
    }
  }

  if (remainingProductIds.length === 0) {
    return productPrices
  }

  const products = await fetchBulkPricesForCustomer(skeem, remainingProductIds, quote.customer?.id)

  for (const [id, customerPrices] of Object.entries(products)) {
    productPrices[id] = suggestedPriceFilter(customerPrices)
  }

  return productPrices
}

function suggestedPriceFilter(customerPrices: ICustomerPrice[]): IProductPrice | null {
  const standardPrice = customerPrices.find(v => v.name.toLowerCase() === "standard")

  // always return universal price unless there is more than 1
  if (customerPrices.filter(x => x.sourceType === "universal_price_band").length === 1) {
    const price = customerPrices.filter(x => x.sourceType === "universal_price_band")[0]
    return {
      sourceName: price.sourceName,
      sourceType: price.sourceType,
      customerPrice: price.price_pennies,
      customerPriceDiscountPercent: price.discount_percent_value,
      usingNonStandardPrice: price.name.toLowerCase() !== "standard",
      standardPrice: standardPrice?.price_pennies,
      currency_name: price?.currency_name,
      applyOpportunityCertificateBreakdown: price?.applyOpportunityCertificateBreakdown,
      requiresApprovalForChange: !!price?.requiresApprovalForChange,
      customerPrices,
    }
  }

  // If there is only 1 shared price, return it
  const sharedPrices = customerPrices.filter(p => p.sourceType === "shared_price_band")
  if (sharedPrices.length === 1) {
    const price = sharedPrices[0]
    return {
      sourceName: price.sourceName,
      sourceType: price.sourceType,
      customerPrice: price.price_pennies,
      customerPriceDiscountPercent: price.discount_percent_value,
      usingNonStandardPrice: price.name.toLowerCase() !== "standard",
      standardPrice: standardPrice?.price_pennies,
      currency_name: price?.currency_name,
      applyOpportunityCertificateBreakdown: price?.applyOpportunityCertificateBreakdown,
      requiresApprovalForChange: !!price?.requiresApprovalForChange,
      customerPrices,
    }
  }

  // Only have 1 price, return it
  if (customerPrices.length === 1) {
    const price = customerPrices[0]

    return {
      sourceName: price.sourceName,
      sourceType: price.sourceType,
      customerPrice: price.price_pennies,
      customerPriceDiscountPercent: price.discount_percent_value,
      usingNonStandardPrice: price.name.toLowerCase() !== "standard",
      standardPrice: standardPrice?.price_pennies,
      currency_name: price?.currency_name,
      applyOpportunityCertificateBreakdown: price?.applyOpportunityCertificateBreakdown,
      requiresApprovalForChange: !!price?.requiresApprovalForChange,
      customerPrices,
    }
  }

  // Use non standard price
  const nonStandardPrices = customerPrices.filter(price => price.name.toLowerCase() !== "standard")
  if (nonStandardPrices.length >= 1) {
    const nonStandardWithPrice = nonStandardPrices.filter(v => (v.price_pennies ?? 0) > 0)
    const preferredPrice = nonStandardWithPrice.filter(p => !!p.preferred_price)
    const preferredPriceFromBands = preferredPrice.filter(p => p.sourceType === "eos_price_band")
    console.log("PRICE", nonStandardWithPrice, preferredPrice, preferredPriceFromBands)
    if (
      preferredPrice.length === 1 ||
      nonStandardWithPrice.length === 1 ||
      preferredPriceFromBands.length === 1
    ) {
      const price = preferredPriceFromBands[0] || preferredPrice[0] || nonStandardWithPrice[0]

      return {
        sourceName: price.sourceName,
        sourceType: price.sourceType,
        customerPrice: price.price_pennies,
        customerPriceDiscountPercent: price.discount_percent_value,
        usingNonStandardPrice: true,
        standardPrice: standardPrice?.price_pennies,
        currency_name: price?.currency_name,
        applyOpportunityCertificateBreakdown: price?.applyOpportunityCertificateBreakdown,
        requiresApprovalForChange: !!price?.requiresApprovalForChange,
        customerPrices,
      }
    }
  }

  // Have multiple prices, but all are standard or universal, return the first universal price
  if (
    customerPrices.filter(
      p => p.name.toLowerCase() === "standard" || p.sourceType === "universal_price_band",
    ).length === customerPrices.length
  ) {
    const prices = customerPrices.filter(p => p.name.toLowerCase() !== "standard")
    const price = prices[0]
    if (!price) {
      return null
    }
    return {
      sourceName: price.sourceName,
      sourceType: price.sourceType,
      customerPrice: price.price_pennies,
      customerPriceDiscountPercent: price.discount_percent_value,
      usingNonStandardPrice: true,
      standardPrice: standardPrice?.price_pennies,
      currency_name: price?.currency_name,
      applyOpportunityCertificateBreakdown: price?.applyOpportunityCertificateBreakdown,
      requiresApprovalForChange: !!price?.requiresApprovalForChange,
      customerPrices,
    }
  }

  return null
}
